/**
 * LoginHistoryPage - User login audit history page
 *
 * Implements VULN-1776 requirement for in-app login audit UI.
 * Displays the authenticated user's login history with timestamps, IP addresses, and status.
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { gql, useQuery } from "@apollo/client";
import { LoginHistoryGrid } from "../components/LoginHistoryGrid";
import { LoginHistory } from "../generated/graphql";

/**
 * GraphQL query to fetch current user's login history
 * Default limit: 20 most recent logins
 */
const GET_LOGIN_HISTORY = gql`
  query GetLoginHistory($limit: Int) {
    myLoginHistory(limit: $limit) {
      id
      loginAt
      ipAddress
      success
    }
  }
`;

/**
 * Page container with consistent styling
 */
const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
`;

/**
 * Page title with border
 */
const PageTitle = styled.h1`
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
`;

/**
 * Loading message
 */
const LoadingMessage = styled.div`
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 4px;
  text-align: center;
  color: #6c757d;
`;

/**
 * Error message
 */
const ErrorMessage = styled.div`
  padding: 20px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  color: #721c24;
`;

/**
 * Empty state message
 */
const EmptyMessage = styled.div`
  padding: 40px 20px;
  background-color: #f8f9fa;
  border-radius: 4px;
  text-align: center;
  color: #6c757d;
`;

/**
 * Build info footer — subtle display of frontend + backend versions
 */
const BuildInfo = styled.div`
  margin-top: 40px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  font-size: 11px;
  color: #aaa;
  display: flex;
  gap: 16px;
`;

/**
 * LoginHistoryPage Component
 *
 * Fetches and displays the current user's login history.
 * Shows loading/error/empty states as appropriate.
 */
/** Frontend git SHA injected by CI at build time (full SHA, display first 7 chars) */
const FRONTEND_VERSION =
  import.meta.env.VITE_BUILD_VERSION?.slice(0, 7) || "dev";

const LoginHistoryPage: React.FC = () => {
  // Fetch login history with default limit of 20
  const { data, loading, error } = useQuery(GET_LOGIN_HISTORY, {
    variables: { limit: 20 },
    fetchPolicy: "cache-and-network", // Always fetch fresh data
  });

  // Fetch backend build version from /api/version endpoint
  const [backendVersion, setBackendVersion] = useState<string>("...");
  useEffect(() => {
    const localDev = window.location.hostname === "localhost";
    const backendPort = import.meta.env.VITE_BACKEND_PORT || "4000";
    const base = localDev ? `http://localhost:${backendPort}` : "";

    fetch(`${base}/api/version`, { credentials: "include" })
      .then((r) => r.json())
      .then((d: { version?: string }) =>
        setBackendVersion(d.version || "unknown"),
      )
      .catch(() => setBackendVersion("unavailable"));
  }, []);

  return (
    <PageContainer>
      <PageTitle>Account Security</PageTitle>

      {loading && <LoadingMessage>Loading login history...</LoadingMessage>}

      {error && (
        <ErrorMessage>
          Failed to load login history: {error.message}
        </ErrorMessage>
      )}

      {!loading && !error && data?.myLoginHistory && (
        <>
          {data.myLoginHistory.length === 0 ? (
            <EmptyMessage>
              No login history available yet. Your login attempts will appear
              here.
            </EmptyMessage>
          ) : (
            <LoginHistoryGrid
              loginHistory={data.myLoginHistory as LoginHistory[]}
            />
          )}
        </>
      )}

      <BuildInfo>
        <span>Frontend: {FRONTEND_VERSION}</span>
        <span>Backend: {backendVersion}</span>
      </BuildInfo>
    </PageContainer>
  );
};

export default LoginHistoryPage;
